import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Award, Globe, Users } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const ProfessionalEdgeTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#E74C3C'; // Default Red
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#2D2D2D', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+123 4567 8910, +234 5678 9102' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'example@mail.com, office@example.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? '123 Road Address, Level-5, NYC' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Risus commodo viverra maecenas accumsan lacus vel facilisis.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>
            {/* Left Sidebar - Dark */}
            <div data-cv-sidebar className="w-[35%] text-white p-8 flex flex-col overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                {/* Profile Photo with Red Border */}
                {(photoSrc || showPlaceholders) && (
                <div className="mb-5 flex justify-center">
                    <div className="w-56 h-64 rounded-t-full rounded-b-3xl border-[6px] overflow-hidden shadow-2xl" style={{ borderColor: imageBorderColor }}>
                        {photoSrc ? (
                            <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                        ) : null}
                    </div>
                </div>
                )}

                {/* Work Skills Section */}
                <section className="mb-8">
                    <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-skills space-y-2">
                        {(data.skills && data.skills.length > 0 ? data.skills.slice(0, 5) : showPlaceholders ? [
                            { name: 'UX Design', level: 70 },
                            { name: 'UI Design', level: 90 },
                            { name: 'User Research', level: 50 },
                            { name: 'Design System', level: 70 },
                            { name: 'Development', level: 35 }
                        ] : []).map((skill, i) => (
                            <div
                                key={skill.id || skill.name || i}
                                data-cv-block
                                data-cv-item-id={skill.id || undefined}
                            >
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs font-bold uppercase tracking-wide">{skill.name}</span>
                                    <span className="text-xs font-bold" style={{ color: primaryColor }}>{skill.level}%</span>
                                </div>
                                <div className="h-1.5 bg-[#444] rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${skill.level}%`, backgroundColor: primaryColor }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Languages Section */}
                <section className="mb-8">
                    <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-languages space-y-4">
                        {(data.languages && data.languages.length > 0 ? data.languages.slice(0, 3) : showPlaceholders ? [
                            { name: 'English (US)', level: 85 },
                            { name: 'English (UK)', level: 70 },
                            { name: 'Hindi (Native)', level: 96 }
                        ] : []).map((lang, i) => (
                            <div
                                key={lang.id || lang.name || i}
                                data-cv-block
                                data-cv-item-id={lang.id || undefined}
                            >
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs font-bold uppercase tracking-wide">{lang.name}</span>
                                    <span className="text-xs font-bold" style={{ color: primaryColor }}>{lang.level}%</span>
                                </div>
                                <div className="h-1.5 bg-[#444] rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full"
                                        style={{ width: `${lang.level}%`, backgroundColor: primaryColor }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Contact Section */}
                {(phone || email || address || showPlaceholders) && (
                <section className="mb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                            <Phone className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-base font-black uppercase tracking-[0.2em]" style={{ color: primaryColor }}>{CV_LABELS.contact}</h2>
                    </div>
                    <div className="cv-fs-contact space-y-3">
                        {phone && (
                        <div>
                            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: primaryColor }}>Phone:</p>
                            <p className="text-xs font-medium break-all">{phone}</p>
                        </div>
                        )}
                        {email && (
                        <div>
                            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: primaryColor }}>Email:</p>
                            <p className="text-xs font-medium break-all">{email}</p>
                        </div>
                        )}
                        {address && (
                        <div>
                            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: primaryColor }}>Address:</p>
                            <p className="text-xs font-medium break-words">{address}</p>
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Reference Section */}
                <section className="mt-auto">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                            <Users className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-base font-black uppercase tracking-[0.2em]" style={{ color: primaryColor }}>{CV_LABELS.references}</h2>
                    </div>
                    {data.references && data.references.length > 0 ? data.references.slice(0, 1).map((ref, i) => (
                        <div
                            key={ref.id || i}
                            className="cv-fs-references"
                            data-cv-block
                            data-cv-item-id={ref.id || undefined}
                        >
                            <p className="text-sm font-black mb-1">{ref.name}</p>
                            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: primaryColor }}>{ref.position} - {ref.company}</p>
                            <p className="text-[10px] mb-1"><span className="font-bold" style={{ color: primaryColor }}>P:</span> {ref.phone}</p>
                            <p className="text-[10px]"><span className="font-bold" style={{ color: primaryColor }}>E:</span> {ref.email}</p>
                        </div>
                    )) : showPlaceholders ? (
                        <div className="cv-fs-references">
                            <p className="text-sm font-black mb-1">Jhon Doe.</p>
                            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: primaryColor }}>Product Manager - Company</p>
                            <p className="text-[10px] mb-1"><span className="font-bold" style={{ color: primaryColor }}>P:</span> +123 4567 8910</p>
                            <p className="text-[10px]"><span className="font-bold" style={{ color: primaryColor }}>E:</span> example@mailname.com</p>
                        </div>
                    ) : null}
                </section>
                    </>
                )}
            </div>

            {/* Right Content - White */}
            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12" style={{ backgroundColor: backgroundColor, color: textColor }}>
                {isContinuationPage ? (
                    <div className="mb-6 pb-3 border-b border-slate-200">
                        <p className="text-sm font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.5 }}>{CV_LABELS.suite}</p>
                    </div>
                ) : (
                <header className="mb-6 border-b-2 pb-6" style={{ borderColor: primaryColor }}>
                    <h1 className="cv-fs-name text-5xl font-black uppercase leading-none mb-2 break-words">
                        <span style={{ color: primaryColor }}>{data.personalInfo.firstName || (showPlaceholders ? 'JOHN' : '')}</span>{' '}
                        <span className="text-slate-900" style={{ color: '#' === textColor[0] ? textColor : '#1e293b' }}>{data.personalInfo.lastName || (showPlaceholders ? 'SMITH.' : '')}</span>
                    </h1>
                    <p className="cv-fs-profession text-sm font-bold uppercase tracking-[0.3em] break-words" style={{ color: textColor, opacity: 0.7 }}>
                        {data.personalInfo.profession || (showPlaceholders ? 'WEB DEVELOPER & DESIGNER' : '')}
                    </p>

                </header>
                )}


                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {summary && (
                <div className="mb-5">
                    <p className="cv-fs-summary text-xs leading-relaxed break-words" style={{ color: textColor, opacity: 0.8 }}>
                        {summary}
                    </p>
                </div>
                )}


                {/* Education Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-education space-y-6 pl-2">
                        {data.education.length > 0 ? data.education.map((edu, i) => (
                            <div
                                key={edu.id || i}
                                className="flex gap-4 relative"
                                data-cv-block
                                data-cv-item-id={edu.id || undefined}
                            >
                                <div className="flex flex-col items-center">
                                    <div className="w-3 h-3 rounded-full border-2 border-white shadow-md shrink-0 mt-1" style={{ backgroundColor: primaryColor }}></div>
                                    {i < data.education.length - 1 && <div className="w-0.5 h-full bg-slate-200 mt-1"></div>}
                                </div>
                                <div className="flex-1 pb-6">
                                    <p className="text-xs font-black mb-1" style={{ color: textColor }}>{edu.startDate} - {frPresent(edu.isCurrent, edu.endDate)}</p>
                                    <h3 className="text-sm font-black uppercase mb-1 break-words" style={{ color: primaryColor }}>{edu.degree}</h3>
                                    <p className="text-xs leading-relaxed break-words" style={{ color: textColor, opacity: 0.7 }}>
                                        {edu.institution}. {edu.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'}
                                    </p>

                                </div>
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div className="flex gap-4">
                                    <div className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: primaryColor }}></div>
                                    <div>
                                        <p className="text-xs font-black text-slate-900 mb-1">2010 - 2015</p>
                                        <h3 className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>VOTRE TITRE ICI</h3>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>

                {/* Work Experience Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-experience space-y-6 pl-2">
                        {data.experience.length > 0 ? data.experience.map((exp, i) => (
                            <div
                                key={exp.id || i}
                                className="flex gap-4"
                                data-cv-block
                                data-cv-item-id={exp.id || undefined}
                            >
                                <div className="flex flex-col items-center">
                                    <div className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: primaryColor }}></div>
                                    {i < data.experience.length - 1 && <div className="w-0.5 h-full bg-slate-200 mt-1"></div>}
                                </div>
                                <div className="flex-1 pb-6">
                                    <p className="text-xs font-black mb-1" style={{ color: textColor }}>{exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}</p>
                                    <h3 className="text-sm font-black uppercase mb-1 break-words" style={{ color: primaryColor }}>{exp.position}</h3>
                                    <p className="text-xs leading-relaxed break-words" style={{ color: textColor, opacity: 0.7 }}>{exp.company}. {exp.description}</p>

                                </div>
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div className="flex gap-4">
                                    <div className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: primaryColor }}></div>
                                    <div>
                                        <p className="text-xs font-black text-slate-900 mb-1">2010 - 2015</p>
                                        <h3 className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>VOTRE TITRE ICI</h3>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>

                {/* Personal Achievement */}
                {showPlaceholders && (
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                            <Award className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-black uppercase tracking-[0.15em]" style={{ color: primaryColor }}>Personal Achievement</h2>
                    </div>
                    <div className="space-y-6 pl-2">
                        <div className="flex gap-4">
                            <div className="w-3 h-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: primaryColor }}></div>
                            <div>
                                <p className="text-xs font-black text-slate-900 mb-1">2010 - 2015</p>
                                <h3 className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>VOTRE TITRE ICI</h3>
                                <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
                            </div>
                        </div>
                    </div>
                </section>
                )}
            </div>
        </div>
    );
};

export default ProfessionalEdgeTemplate;
